/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a store key, header, status or wire code; it renders nothing */
// W3 per-owner snapshots in the fenced owner database (C5):
//
//   decks       one record per saved deck row (rev, card_meta, the stored
//               analysis and its date), so the decks list and the deck pages
//               read offline
//   deck_cards  card metadata (the /cards/images answer) for every card in
//               every stored deck: what useCardInfoStore and the editor's
//               stats and legality read
//   collection  the A1 snapshot (GET /collections/{id}/snapshot): lines,
//               binders (manual and smart, materialized), sealed, with its
//               strong ETag; or the too_large marker
//   meta        per-item C6 status
//
// Every write is a fencedWrite with the context the writer captured.
import { CONN, META_COLLECTION, META_DECKS, META_DECK_CARDS, META_PROFILE, SCHEMA_VERSION, SNAPSHOT_STATUS, STORE, type ConnectivityState } from './offline.codes'
import { fencedWrite, openOwnerDb, ownerGet, ownerGetAll, isQuotaError, req, type FencedResult, type WriterCtx } from './offlineStore'
import { rfc3339 } from './time'
import type { SavedDeck } from '../../hooks/useSavedDecks'
import { cardKey, type CardImage } from '../../api/cardImages'
import { answerableSorts, buildFirstPages, coversSorts, firstPageRecordKey, readFirstPageRecord, writtenUnder, type FirstPageSort, type StoredFirstPages } from './firstPage'

// ---- Decks ----------------------------------------------------------------------------------

export interface StoredDeck {
  schema_version: number
  deck: SavedDeck
  stored_at: string
}

export interface ItemStatus {
  schema_version: number
  /** Last successful refresh (ready / stale since). */
  refreshed_at: string | null
  /** Last attempt that failed, with its code (storage_full, server_error, forbidden). */
  error?: { code: string; at: string } | null
  /** deck_card_data: names the last refresh could not resolve. */
  missing?: number
  count?: number
}

/** A complete list replaces the store; a single landed row is upserted. */
export function writeDeckSnapshot(ctx: WriterCtx, decks: SavedDeck[], opts: { complete: boolean }, now = new Date()): Promise<FencedResult> {
  return fencedWrite(ctx, [STORE.decks, STORE.meta], tx => {
    const s = tx.objectStore(STORE.decks)
    if (opts.complete) s.clear()
    for (const d of decks) {
      const rec: StoredDeck = { schema_version: SCHEMA_VERSION, deck: d, stored_at: rfc3339(now) }
      s.put(rec, d.id)
    }
    if (opts.complete) {
      const status: ItemStatus = { schema_version: SCHEMA_VERSION, refreshed_at: rfc3339(now), error: null, count: decks.length }
      tx.objectStore(STORE.meta).put(status, META_DECKS)
    }
  })
}

export function removeDeckFromSnapshot(ctx: WriterCtx, deckId: string): Promise<FencedResult> {
  return fencedWrite(ctx, [STORE.decks], tx => { tx.objectStore(STORE.decks).delete(deckId) })
}

/** The stored decks (newest touched first), or null when never stored. */
export async function readDeckSnapshot(ownerKey: string): Promise<{ decks: SavedDeck[]; refreshedAt: string | null } | null> {
  const status = await ownerGet<ItemStatus>(ownerKey, STORE.meta, META_DECKS)
  const rows = await ownerGetAll<StoredDeck>(ownerKey, STORE.decks)
  if (!status && rows.length === 0) return null
  // A record of another schema is cache: discarded (refetched online).
  const decks = rows.filter(r => r.schema_version === SCHEMA_VERSION && r.deck?.id).map(r => r.deck)
  decks.sort((a, b) => (a.savedAt < b.savedAt ? 1 : a.savedAt > b.savedAt ? -1 : 0))
  return { decks, refreshedAt: status?.refreshed_at ?? null }
}

// ---- Deck card metadata -------------------------------------------------------------------------

export interface StoredCard {
  schema_version: number
  info: CardImage
  /** When it was fetched (refreshed weekly). */
  fetched_at?: string
}

/** Every key a deck list may use for a card: the canonical name, a DFC front face, each face. */
export function cardKeysOf(info: CardImage): string[] {
  const keys = new Set<string>([cardKey(info.name)])
  if (info.name.includes(' // ')) keys.add(cardKey(info.name.split(' // ')[0]))
  for (const f of info.faces ?? []) if (f?.name) keys.add(cardKey(f.name))
  return [...keys]
}

export function writeDeckCards(ctx: WriterCtx, infos: CardImage[], status: { missing: number } | null, prune: string[] = [], now = new Date()): Promise<FencedResult> {
  return fencedWrite(ctx, [STORE.deckCards, STORE.meta], tx => {
    const s = tx.objectStore(STORE.deckCards)
    for (const k of prune) s.delete(k)
    for (const info of infos) {
      const rec: StoredCard = { schema_version: SCHEMA_VERSION, info, fetched_at: rfc3339(now) }
      for (const k of cardKeysOf(info)) s.put(rec, k)
    }
    if (status) {
      const st: ItemStatus = { schema_version: SCHEMA_VERSION, refreshed_at: rfc3339(now), error: null, missing: status.missing }
      tx.objectStore(STORE.meta).put(st, META_DECK_CARDS)
    }
  })
}

export async function readDeckCards(ownerKey: string): Promise<Map<string, CardImage>> {
  const db = await ownerGetAllWithKeys<StoredCard>(ownerKey, STORE.deckCards)
  const out = new Map<string, CardImage>()
  for (const [k, v] of db) if (v?.schema_version === SCHEMA_VERSION && v.info) out.set(String(k), v.info)
  return out
}

/** Every stored entry with its fetch date (the weekly refresh and pruning). */
export async function readDeckCardAges(ownerKey: string): Promise<Map<string, { info: CardImage; fetched_at: string | null }>> {
  const out = new Map<string, { info: CardImage; fetched_at: string | null }>()
  for (const [k, v] of await ownerGetAllWithKeys<StoredCard>(ownerKey, STORE.deckCards)) {
    if (v?.schema_version === SCHEMA_VERSION && v.info) out.set(String(k), { info: v.info, fetched_at: v.fetched_at ?? null })
  }
  return out
}

async function ownerGetAllWithKeys<T>(ownerKey: string, store: typeof STORE.deckCards): Promise<[IDBValidKey, T][]> {
  const db = await openOwnerDb(ownerKey)
  if (!db) return []
  try {
    const tx = db.transaction(store, 'readonly')
    const os = tx.objectStore(store)
    const [keys, values] = await Promise.all([req(os.getAllKeys()), req(os.getAll())])
    return keys.map((k, i) => [k, values[i] as T])
  } catch {
    return []
  }
}

// ---- Account profile (what the header shows offline) ---------------------------------------------

export interface StoredProfile {
  schema_version: number
  id: string
  email: string | null
  user_metadata: Record<string, unknown>
}

export function writeProfile(ctx: WriterCtx, p: Omit<StoredProfile, 'schema_version'>): Promise<FencedResult> {
  return fencedWrite(ctx, [STORE.meta], tx => {
    tx.objectStore(STORE.meta).put({ schema_version: SCHEMA_VERSION, ...p }, META_PROFILE)
  })
}

export async function readProfile(ownerKey: string): Promise<StoredProfile | null> {
  const p = await ownerGet<StoredProfile>(ownerKey, STORE.meta, META_PROFILE)
  return p && p.schema_version === SCHEMA_VERSION && typeof p.id === 'string' ? p : null
}

// ---- A1 collection snapshot ------------------------------------------------------------------------

export interface SnapshotLine {
  copy_id: number
  card_name: string
  name_key: string
  oracle_id?: string | null
  scryfall_id: string | null
  set: string | null
  set_name: string | null
  collector_number: string | null
  finish: string
  qty: number
  condition: string | null
  language: string | null
  status: string
  purchase_price: string | null
  purchase_currency: string | null
  source: string
  added_at: string
  rarity: string | null
  type_line: string | null
  colors: string[] | null
  color_identity: string[] | null
  mv: number | null
  image_small?: string | null
  image_normal: string | null
  unit_price_usd: number | null
  /**
   * The exact line price the server's value and gain sorts compare
   * (LINE_PRICE_SQL, name fallback included); null where the server's is
   * NULL. Absent from a snapshot built by an older API.
   */
  sort_price_usd?: number | null
  binder_id: number | null
  smart_binder_ids?: number[]
  /**
   * The 7D market change in percentage points, computed at snapshot build
   * (collection views API); absent from older snapshots. Offline it shows
   * only while the snapshot is at most 2 days old (offlineChangeFresh).
   */
  price_change_7d_pct?: number | null
  price_change_base_day?: string | null
}

export interface SnapshotBinder {
  binder_id: number
  kind: string
  name: string
  color?: string | null
  cover?: unknown
  position?: number
  updated_at?: string
  rules?: unknown
  rules_rev?: number
  [k: string]: unknown
}

export interface SnapshotOk {
  status: 'ok'
  schema_version: number
  collection_id: string
  copies_rev: number
  as_of: string
  line_count: number
  deck_boxes_included: boolean
  lines: SnapshotLine[]
  binders: SnapshotBinder[]
  sealed: unknown[]
}

export interface SnapshotTooLarge {
  status: 'too_large'
  line_count: number
  limit: number
}

export interface StoredCollection {
  schema_version: number
  collection_id: string
  etag: string | null
  body: SnapshotOk | null
  too_large: { line_count: number; limit: number } | null
  fetched_at: string
  checked_at: string
}

/** A1 body validation (schema tests use the fixture examples). */
export function parseSnapshot(raw: unknown): SnapshotOk | SnapshotTooLarge | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  if (o.status === SNAPSHOT_STATUS.tooLarge) {
    if (typeof o.line_count !== 'number' || typeof o.limit !== 'number') return null
    return { status: 'too_large', line_count: o.line_count, limit: o.limit }
  }
  if (o.status !== SNAPSHOT_STATUS.ok) return null
  if (typeof o.collection_id !== 'string' || !Array.isArray(o.lines) || !Array.isArray(o.binders)) return null
  if (typeof o.schema_version === 'number' && o.schema_version !== SCHEMA_VERSION) return null
  return {
    status: 'ok',
    schema_version: SCHEMA_VERSION,
    collection_id: o.collection_id,
    copies_rev: typeof o.copies_rev === 'number' ? o.copies_rev : 0,
    as_of: typeof o.as_of === 'string' ? o.as_of : '',
    line_count: typeof o.line_count === 'number' ? o.line_count : o.lines.length,
    deck_boxes_included: o.deck_boxes_included === true,
    lines: o.lines as SnapshotLine[],
    binders: o.binders as SnapshotBinder[],
    sealed: Array.isArray(o.sealed) ? o.sealed : [],
  }
}

function fenceOf(ctx: WriterCtx) {
  return { ownerKey: ctx.ownerKey, incarnation: ctx.incarnation, fenceEpoch: ctx.fenceEpoch }
}

export async function readCollectionSnapshot(ownerKey: string, collectionId: string): Promise<StoredCollection | null> {
  const rec = await ownerGet<StoredCollection>(ownerKey, STORE.collection, collectionId)
  if (!rec || rec.schema_version !== SCHEMA_VERSION) return null
  return rec
}

export type CollectionRefreshResult =
  | { result: 'stored' | 'not_modified' | 'too_large' }
  /** Retry later (snapshot_busy, 429, offline, 5xx). */
  | { result: 'transient'; retryAfterS: number | null }
  | { result: 'needs_session' }
  | { result: 'error'; code: 'forbidden' | 'not_found' | 'server_error' | 'storage_full' }
  /** The fence moved while it ran: nothing written. */
  | { result: 'refused' }

export interface CollectionFetch {
  (url: string, init: RequestInit): Promise<Response>
}

/**
 * Refresh one collection's A1 snapshot: If-None-Match with the stored ETag;
 * 304 keeps it (checked now); 200 ok replaces it; 200 too_large DROPS any
 * older snapshot (C6 inv. 3); 503 snapshot_busy and 429 are transient;
 * 401 needs the session; 403 / 404 / 409 are errors kept in meta.
 *
 * The provisional first page (firstPage.ts) is written in the SAME fenced
 * transaction as the body it was computed from, for `firstPageSorts`: a 200
 * ok replaces it, a 304 fills in a sort the stored record lacks, too_large
 * drops it. `yieldToIdle` runs before each main-thread heavy step (reading
 * the stored body, parsing the new one, building the pages), so an open
 * screen's own work goes first.
 */
export async function refreshCollectionSnapshot(args: {
  ctx: WriterCtx
  collectionId: string
  url: string
  token: string
  fetch: CollectionFetch
  now?: () => Date
  firstPageSorts?: readonly FirstPageSort[]
  yieldToIdle?: () => Promise<void>
  /**
   * Resolves once nothing holds the refresh back (an open screen reading its
   * first page). Awaited after the stored-snapshot read, and the download
   * starts in the same task it resolves in (no await between).
   */
  whenUnheld?: () => Promise<void>
  /** A hold is taken right now (checked synchronously right before the request). */
  isHeld?: () => boolean
  /** Test seam: the stored-snapshot read. */
  readStored?: typeof readCollectionSnapshot
}): Promise<CollectionRefreshResult> {
  const now = args.now ?? (() => new Date())
  const idle = args.yieldToIdle ?? (() => Promise.resolve())
  const whenUnheld = args.whenUnheld ?? (() => Promise.resolve())
  const readStored = args.readStored ?? readCollectionSnapshot
  const sorts = answerableSorts(args.firstPageSorts ?? [])
  const pagesKey = firstPageRecordKey(args.collectionId)
  await idle()
  const prior = await readStored(args.ctx.ownerKey, args.collectionId)
  const headers: Record<string, string> = { Authorization: `Bearer ${args.token}`, 'Cache-Control': 'no-store' }
  if (prior?.etag && prior.body) headers['If-None-Match'] = prior.etag
  // A hold taken while the stored copy was read: wait it out. Nothing is
  // awaited between this and the request, so no hold can slip in.
  do { await whenUnheld() } while (args.isHeld?.())
  let res: Response
  try {
    res = await args.fetch(args.url, { method: 'GET', headers, cache: 'no-store' })
  } catch {
    return { result: 'transient', retryAfterS: null }
  }
  const at = rfc3339(now())
  const markError = async (code: 'forbidden' | 'not_found' | 'server_error' | 'storage_full'): Promise<CollectionRefreshResult> => {
    await fencedWrite(args.ctx, [STORE.meta], tx => {
      const st: ItemStatus = { schema_version: SCHEMA_VERSION, refreshed_at: prior?.checked_at ?? null, error: { code, at } }
      tx.objectStore(STORE.meta).put(st, META_COLLECTION)
    }).catch(() => 'refused')
    return { result: 'error', code }
  }
  if (res.status === 304 && prior) {
    // The stored record already answers every wanted sort for this body, or
    // it is rebuilt from the stored body (a sort chosen since the last 200).
    // A record of another fence (an older epoch or incarnation) is rebuilt
    // under this one, or dropped; never kept.
    let pages: StoredFirstPages | null = null
    let drop = false
    const have = await readFirstPageRecord(args.ctx.ownerKey, args.collectionId)
    if (prior.body && sorts.length) {
      if (!coversSorts(have, prior.etag, prior.body, sorts, fenceOf(args.ctx))) {
        await idle()
        pages = buildFirstPages({ fence: fenceOf(args.ctx), body: prior.body, etag: prior.etag, sorts })
      }
    } else if (have && !writtenUnder(have, fenceOf(args.ctx))) {
      drop = true
    }
    const w = await fencedWrite(args.ctx, [STORE.collection, STORE.meta], tx => {
      tx.objectStore(STORE.collection).put({ ...prior, checked_at: at }, args.collectionId)
      if (pages) tx.objectStore(STORE.collection).put(pages, pagesKey)
      else if (drop) tx.objectStore(STORE.collection).delete(pagesKey)
      const st: ItemStatus = { schema_version: SCHEMA_VERSION, refreshed_at: at, error: null }
      tx.objectStore(STORE.meta).put(st, META_COLLECTION)
    })
    return w === 'ok' ? { result: 'not_modified' } : { result: 'refused' }
  }
  if (res.status === 401) return { result: 'needs_session' }
  if (res.status === 429 || res.status === 503) {
    const ra = res.headers.get('Retry-After')
    return { result: 'transient', retryAfterS: ra && /^\d+$/.test(ra.trim()) ? parseInt(ra, 10) : null }
  }
  if (res.status === 403) return markError('forbidden')
  if (res.status === 404 || res.status === 409) return markError('not_found')
  if (res.status >= 500) return { result: 'transient', retryAfterS: null }
  if (res.status !== 200) return markError('server_error')
  let body: unknown
  try {
    await idle()
    body = await res.json()
  } catch {
    return { result: 'transient', retryAfterS: null }
  }
  const parsed = parseSnapshot(body)
  if (!parsed) return markError('server_error')
  let pages: StoredFirstPages | null = null
  if (parsed.status === 'ok' && sorts.length) {
    await idle()
    pages = buildFirstPages({ fence: fenceOf(args.ctx), body: parsed, etag: res.headers.get('ETag'), sorts })
  }
  const rec: StoredCollection = parsed.status === 'too_large'
    ? { schema_version: SCHEMA_VERSION, collection_id: args.collectionId, etag: null, body: null, too_large: { line_count: parsed.line_count, limit: parsed.limit }, fetched_at: at, checked_at: at }
    : { schema_version: SCHEMA_VERSION, collection_id: args.collectionId, etag: res.headers.get('ETag'), body: parsed, too_large: null, fetched_at: at, checked_at: at }
  try {
    const w = await fencedWrite(args.ctx, [STORE.collection, STORE.meta], tx => {
      tx.objectStore(STORE.collection).put(rec, args.collectionId)
      // The first page always belongs to the body stored with it (none for too_large).
      if (pages) tx.objectStore(STORE.collection).put(pages, pagesKey)
      else tx.objectStore(STORE.collection).delete(pagesKey)
      const st: ItemStatus = { schema_version: SCHEMA_VERSION, refreshed_at: at, error: null, count: parsed.line_count }
      tx.objectStore(STORE.meta).put(st, META_COLLECTION)
    })
    if (w !== 'ok') return { result: 'refused' }
  } catch (e) {
    if (isQuotaError(e)) return markError('storage_full')
    throw e
  }
  return { result: parsed.status === 'too_large' ? 'too_large' : 'stored' }
}

export function readItemStatus(ownerKey: string, key: string): Promise<ItemStatus | null> {
  return ownerGet<ItemStatus>(ownerKey, STORE.meta, key)
}

/** Lines of one binder, as of the snapshot: manual by location, smart by materialized membership. */
export function linesOfBinder(snap: SnapshotOk, binder: SnapshotBinder): SnapshotLine[] {
  if (binder.kind === 'smart') return snap.lines.filter(l => (l.smart_binder_ids ?? []).includes(binder.binder_id))
  return snap.lines.filter(l => l.binder_id === binder.binder_id)
}

export function isStale(iso: string | null | undefined, now = Date.now(), maxMs = 24 * 60 * 60 * 1000): boolean {
  if (!iso) return true
  const t = Date.parse(iso)
  return !Number.isFinite(t) || now - t > maxMs
}

export function connectivityAllowsRefresh(c: ConnectivityState): boolean {
  return c === CONN.online
}
